import React from 'react';

const PriceHistoryChart = ({ scrapedListings, listing, estimatedHistory }) => {
  // Get price history - combine real data + AI estimates
  const getPriceHistory = () => {
    // Use passed estimatedHistory from alert-level (for initial load)
    // Or listing-level estimated_price_history (for per-listing estimates)
    const estimates = estimatedHistory || listing?.estimated_price_history || [];
    
    if (!scrapedListings || scrapedListings.length === 0) {
      // No real data, use estimates only
      if (estimates.length > 0) {
        return estimates.map(point => {
          // Format date properly
          const date = new Date(point.date);
          const formattedDate = date.toLocaleDateString('en-US', { 
            month: 'short', 
            year: 'numeric' 
          });
          
          return {
            date: formattedDate,
            average: point.price,
            upper: point.price * 1.05,  // Add 5% upper band
            lower: point.price * 0.95,  // Add 5% lower band
            count: 1,
            isEstimated: point.is_estimated !== false
          };
        });
      }
      return [];
    }

    // Group real data by date
    const byDate = {};
    scrapedListings.forEach(listing => {
      const date = new Date(listing.scraped_at).toLocaleDateString();
      if (!byDate[date]) {
        byDate[date] = [];
      }
      if (listing.price > 0) {
        byDate[date].push(listing.price);
      }
    });

    // Calculate bands for each date
    const realHistory = Object.entries(byDate).map(([date, prices]) => {
      prices.sort((a, b) => a - b);
      return {
        date,
        upper: Math.max(...prices),
        average: prices.reduce((a, b) => a + b, 0) / prices.length,
        lower: Math.min(...prices),
        count: prices.length,
        isEstimated: false
      };
    });
    
    // If we have limited real history, prepend estimates
    if (realHistory.length < 5 && estimatedHistory.length > 0) {
      const estimatedPoints = estimatedHistory.map(point => {
        const date = new Date(point.date);
        const formattedDate = date.toLocaleDateString('en-US', { 
          month: 'short', 
          year: 'numeric' 
        });
        
        return {
          date: formattedDate,
          average: point.price,
          upper: point.price * 1.05,
          lower: point.price * 0.95,
          count: 1,
          isEstimated: point.is_estimated !== false
        };
      });
      
      // Combine estimates + real data
      return [...estimatedPoints, ...realHistory].sort((a, b) => new Date(a.date) - new Date(b.date));
    }

    return realHistory.sort((a, b) => new Date(a.date) - new Date(b.date));
  };

  const history = getPriceHistory();

  if (history.length === 0) {
    return (
      <div className="text-center py-8 text-gray-500">
        <p>No price data available</p>
      </div>
    );
  }

  // Chart dimensions
  const width = 340;
  const height = 200;
  const padding = { top: 20, right: 10, bottom: 30, left: 50 };
  const chartWidth = width - padding.left - padding.right;
  const chartHeight = height - padding.top - padding.bottom;

  // Find min/max for scaling
  const allPrices = history.flatMap(h => [h.upper, h.average, h.lower]);
  const minPrice = Math.min(...allPrices);
  const maxPrice = Math.max(...allPrices);
  const priceRange = maxPrice - minPrice || 1;

  // Scale functions
  const scaleX = (index) => padding.left + (index / Math.max(history.length - 1, 1)) * chartWidth;
  const scaleY = (price) => padding.top + chartHeight - ((price - minPrice) / priceRange) * chartHeight;

  // Create path strings
  const createPath = (type) => {
    return history.map((h, i) => {
      const x = scaleX(i);
      const y = scaleY(h[type]);
      return i === 0 ? `M ${x} ${y}` : `L ${x} ${y}`;
    }).join(' ');
  };

  const upperPath = createPath('upper');
  const averagePath = createPath('average');
  const lowerPath = createPath('lower');

  // Y-axis labels
  const yLabels = [
    { value: maxPrice, label: `$${maxPrice.toFixed(0)}` },
    { value: (maxPrice + minPrice) / 2, label: `$${((maxPrice + minPrice) / 2).toFixed(0)}` },
    { value: minPrice, label: `$${minPrice.toFixed(0)}` }
  ];

  return (
    <div className="w-full">
      <svg width={width} height={height} className="w-full" viewBox={`0 0 ${width} ${height}`}>
        {/* Grid lines */}
        {yLabels.map((label, i) => {
          const y = scaleY(label.value);
          return (
            <g key={i}>
              <line
                x1={padding.left}
                y1={y}
                x2={width - padding.right}
                y2={y}
                stroke="#e5e7eb"
                strokeWidth="1"
                strokeDasharray="4 4"
              />
              <text
                x={padding.left - 5}
                y={y + 4}
                textAnchor="end"
                className="text-xs fill-gray-500"
              >
                {label.label}
              </text>
            </g>
          );
        })}

        {/* Upper band line (red) */}
        <path
          d={upperPath}
          fill="none"
          stroke="#ef4444"
          strokeWidth="2"
          strokeLinecap="round"
        />

        {/* Average line (blue) - dashed if estimated */}
        <path
          d={averagePath}
          fill="none"
          stroke="#3b82f6"
          strokeWidth="3"
          strokeLinecap="round"
          strokeDasharray={history.some(h => h.isEstimated) ? "5 5" : "none"}
          opacity={history.some(h => h.isEstimated) ? "0.7" : "1"}
        />

        {/* Lower band line (green) */}
        <path
          d={lowerPath}
          fill="none"
          stroke="#10b981"
          strokeWidth="2"
          strokeLinecap="round"
        />

        {/* Data points */}
        {history.map((h, i) => {
          const x = scaleX(i);
          return (
            <g key={i}>
              <circle cx={x} cy={scaleY(h.upper)} r="3" fill="#ef4444" />
              <circle cx={x} cy={scaleY(h.average)} r="4" fill="#3b82f6" />
              <circle cx={x} cy={scaleY(h.lower)} r="3" fill="#10b981" />
            </g>
          );
        })}

        {/* X-axis labels - Show years only */}
        {history.map((h, i) => {
          const x = scaleX(i);
          
          // Show year labels every 3 months or at start/end
          const showLabel = i === 0 || i === history.length - 1 || i % 3 === 0;
          if (!showLabel) return null;
          
          // Extract year from date string
          const dateStr = h.date;
          const year = dateStr.includes('2024') ? '2024' : 
                       dateStr.includes('2025') ? '2025' : 
                       dateStr.includes('2026') ? '2026' : 
                       dateStr.split(' ')[1] || dateStr;
          
          return (
            <text
              key={i}
              x={x}
              y={height - 10}
              textAnchor="middle"
              className="text-xs fill-gray-500 font-medium"
            >
              {year}
            </text>
          );
        })}
      </svg>

      {/* Legend */}
      <div className="flex justify-center items-center space-x-4 mt-3 text-xs flex-wrap gap-2">
        <div className="flex items-center space-x-1">
          <div className="w-3 h-3 rounded-full bg-red-500"></div>
          <span className="text-gray-600">Upper Band</span>
        </div>
        <div className="flex items-center space-x-1">
          <div className="w-3 h-3 rounded-full bg-blue-500"></div>
          <span className="text-gray-600">Average</span>
        </div>
        <div className="flex items-center space-x-1">
          <div className="w-3 h-3 rounded-full bg-green-500"></div>
          <span className="text-gray-600">Lower Band</span>
        </div>
        {history.some(h => h.isEstimated) && (
          <div className="flex items-center space-x-1">
            <div className="w-6 h-0.5 bg-blue-500 opacity-60" style={{borderTop: '2px dashed #3b82f6'}}></div>
            <span className="text-gray-500">AI Estimated</span>
          </div>
        )}
      </div>

      {/* Stats below chart */}
      <div className="grid grid-cols-3 gap-2 mt-4 text-center text-xs">
        <div>
          <p className="text-gray-500">Highest</p>
          <p className="font-bold text-red-600">S${Math.max(...history.map(h => h.upper)).toFixed(0)}</p>
        </div>
        <div>
          <p className="text-gray-500">Avg Range</p>
          <p className="font-bold text-blue-600">
            S${(history.reduce((sum, h) => sum + h.average, 0) / history.length).toFixed(0)}
          </p>
        </div>
        <div>
          <p className="text-gray-500">Lowest</p>
          <p className="font-bold text-green-600">S${Math.min(...history.map(h => h.lower)).toFixed(0)}</p>
        </div>
      </div>
    </div>
  );
};

export default PriceHistoryChart;
